// Home — the bento dashboard, ported from design_handoff_oreia/reference/home-screen.jsx.
//
// The handoff had two states: a populated dashboard and a first-run screen. Both are real here, and
// which one you get depends on whether you've written anything. The cards read from live data, and
// a card with nothing to say is omitted rather than filled with a placeholder — a dashboard of
// invented numbers would undermine the one thing this product is selling, which is that the
// patterns it shows you are actually yours.

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSession } from '../auth/SessionProvider';
import { PersonalPractice } from '../components/PersonalPractice';
import { useEntries } from '../entries/EntriesProvider';
import { usePortrait } from '../portrait/usePortrait';
import { Spinner } from '../components/ui';
import {
  IconArrow,
  IconArrowUpRight,
  IconLock,
  IconSliders,
  IconSpark,
  IconWave,
} from '../components/Icons';
import {
  countedSymbols,
  greetingFor,
  publishedEntries,
  relativeTime,
  writingRhythm,
  type Rhythm,
} from './home-support';
import { SymbolOrb } from '../symbols/SymbolOrb';
import { patternLabel } from '../api/labels';
import { CardArt } from '../components/CardArt';
import { useHomeSignals } from './useHomeSignals';
import { ThreadRow } from '../threads/ThreadRow';
import type { Pattern, PortraitChanges } from '../api/portrait';

export function HomeScreen() {
  const { user } = useSession();
  const { entries, isLoading } = useEntries();
  const navigate = useNavigate();
  const portrait = usePortrait();

  const firstName = (user?.name || user?.email?.split('@')[0] || 'there').split(' ')[0];

  // Every count on this dashboard is a link into a filtered feed, and those feeds hold drafts out.
  // Counting drafts here would make the tile disagree with the page it opens, which is the one thing
  // a number like this must not do.
  const published = useMemo(() => publishedEntries(entries), [entries]);
  const dreams = useMemo(() => published.filter((e) => e.kind === 'dream'), [published]);
  const symbols = useMemo(() => countedSymbols(published), [published]);
  const rhythm = useMemo(() => writingRhythm(published), [published]);
  const { threads, changes } = useHomeSignals();
  const latest = entries[0];

  if (isLoading) {
    return (
      <div style={{ paddingTop: 80 }}>
        <Spinner label="Gathering your entries…" />
      </div>
    );
  }

  if (entries.length === 0) {
    return <FirstRun name={firstName} onCompose={() => navigate('/write/conversation',{state:{conversation:true}})} />;
  }

  return (
    <>
      <Header />

      <div className="ogreet">
        <h1>
          {greetingFor(new Date())},<em>{firstName}.</em>
        </h1>
        <div className="gsub">
          {latest ? (
            <>
              <p>
                You last wrote about <em>{latest.title || 'something unnamed'}</em>.
              </p>
              <span className="omono">{relativeTime(latest.createdAt)}</span>
            </>
          ) : null}
        </div>
      </div>

      <nav className="ohome-actions" aria-label="Dream journal shortcuts">
        <Link className="ohome-action is-primary" to="/write" state={{ seed: { kind: 'dream' } }}><IconSpark /> Record a dream</Link>
        <Link className="ohome-action" to="/journal?filter=dream">Explore your dreams</Link>
      </nav>

      <PersonalPractice />
      <div className="obento">
        {portrait.observation ? (
          <div className="ocard ocard-bright span2">
            <div className="ohead-row">
              <span className="olabel">Higher Self</span>
              <IconWave style={{ width: 22, height: 22, color: 'var(--ink-mute)' }} />
            </div>
            <h3 className="otitle" style={{ fontSize: 26 }}>
              {portrait.observation}
            </h3>
            {/* Carries the observation over as router state rather than a query parameter. It is
                decrypted private writing, and a query parameter would copy it into browser history
                and into the Referer header of every request the next page makes. */}
            <Link
              className="orow-link"
              to="/oreia"
              state={{ prompt: `You said: "${portrait.observation}" — can we talk about that?` }}
              style={{ marginTop: 16 }}
            >
              Talk it through
              <span className="ocirc">
                <IconArrow />
              </span>
            </Link>
          </div>
        ) : (
          <div className="ocard ocard-bright span2">
            <div className="ohead-row">
              <span className="olabel">Higher Self</span>
              <IconWave style={{ width: 22, height: 22, color: 'var(--ink-mute)' }} />
            </div>
            <h3 className="otitle" style={{ fontSize: 24 }}>
              Ask Oreia what it sees in <em>your own words.</em>
            </h3>
            <Link className="orow-link" to="/oreia" style={{ marginTop: 16 }}>
              Open the conversation
              <span className="ocirc">
                <IconArrow />
              </span>
            </Link>
          </div>
        )}

        {/* A number the app worked out about you should be a way in, not a readout. Both tiles are
            whole-card links: the count is the interesting part, but it is a small tap target. */}
        {dreams.length > 0 ? (
          <Link className="ocard ocard-deep ocard-link" to="/journal?filter=dream">
            <CardArt variant="dreams" />
            <div className="ohead-row">
              <span className="otag">
                <span className="dot" />
                Dreams
              </span>
            </div>
            <div className="ostat">{dreams.length}</div>
            <div className="olabel" style={{ marginTop: 8, color: 'var(--ink-soft)' }}>
              recorded
            </div>
            <div className="ometa" style={{ marginTop: 14, fontSize: 11 }}>
              <IconArrowUpRight style={{ width: 13, height: 13 }} /> {relativeTime(dreams[0].createdAt)}
            </div>
          </Link>
        ) : null}

        <Link className="ocard ocard-light ocard-link" to="/journal?view=calendar">
          <CardArt variant="rhythm" />
          <div className="ohead-row">
            <span className="olabel">Rhythm</span>
          </div>
          <RhythmGrid rhythm={rhythm} />
          <div className="orhythm-foot">
            <div>
              <div className="orhythm-total">{rhythm.total}</div>
              <div className="olabel" style={{ marginTop: 4, color: 'var(--ink-soft)' }}>
                {rhythm.total === 1 ? 'entry' : 'entries'}
              </div>
            </div>
            {rhythm.lastWroteAt ? (
              <div className="omono orhythm-last">
                last
                <br />
                {shortDate(rhythm.lastWroteAt)}
              </div>
            ) : null}
          </div>
        </Link>

        {threads.length > 0 ? (
          <div className="ocard ocard-light span2">
            <div className="ohead-row">
              <span className="olabel">Threads</span>
              <Link className="omono osym-all" to="/threads">
                {threads.length} found
              </Link>
            </div>
            {threads.slice(0, 2).map((thread) => (
              <ThreadRow key={thread.id} thread={thread} />
            ))}
            {threads.length > 2 ? (
              <Link className="othread-all" to="/threads">
                See all {threads.length} threads
              </Link>
            ) : null}
          </div>
        ) : null}

        {portrait.insight ? (
          <InsightCard insight={portrait.insight} pattern={portrait.insightPattern} />
        ) : null}

        <WhileYouWereAway changes={changes} />

        {symbols.length > 0 ? (
          <div className="ocard ocard-light span2">
            <div className="ohead-row">
              <span className="olabel">Symbol Vault</span>
              <Link className="omono osym-all" to="/symbols">
                {symbols.length} tracked
              </Link>
            </div>
            <div className="osyms">
              {symbols.slice(0, 4).map((s) => (
                <Link className="osym osym-link" key={s.name} to={`/symbols/${encodeURIComponent(s.name)}`}>
                  <SymbolOrb name={s.name} />
                  <div className="snm">{s.name}</div>
                  <div className="smono">×{s.count}</div>
                </Link>
              ))}
            </div>
          </div>
        ) : null}

        {(() => {
          // Motif constellation: symbols + patterns as signal nodes — not a fixed archetype identity.
          const motifNodes = [
            ...symbols.slice(0, 5).map((s) => ({ id: s.name, label: s.name, weight: s.count })),
            ...portrait.patterns
              .filter(
                (p) =>
                  !symbols.some(
                    (s) =>
                      s.name.toLowerCase() === patternNodeLabel(p.canonicalId).toLowerCase(),
                  ),
              )
              .slice(0, 3)
              .map((p) => ({
                id: p.canonicalId,
                label: patternNodeLabel(p.canonicalId),
                weight: p.occurrences,
              })),
          ].slice(0, 5);
          if (motifNodes.length === 0) return null;
          return (
            <section className="ocard osignal span2" aria-label="Signal constellation">
              <div className="ohead-row">
                <span className="olabel">Signal constellation</span>
                <Link className="osignal-open" to="/self" aria-label="Open Self Discovery">
                  <IconSliders style={{ width: 18, height: 18 }} />
                </Link>
              </div>
              <h3 className="otitle osignal-title">What keeps returning.</h3>
              <p className="osignal-caption">Symbols and themes appearing in your entries.</p>
              <div className="osignal-grid">
                {motifNodes.map((node, index) => {
                  const symbol = symbols.find((item) => item.name === node.id);
                  return (
                    <Link className={`osignal-node${index === 0 ? ' is-leading' : ''}`} key={node.id}
                      to={symbol ? `/symbols/${encodeURIComponent(symbol.name)}` : '/self'}>
                      <SymbolOrb name={node.label} />
                      <span className="osignal-name">{node.label}</span>
                      <span className="osignal-count">{node.weight} {node.weight === 1 ? 'appearance' : 'appearances'}</span>
                      {index === 0 ? <IconArrow /> : null}
                    </Link>
                  );
                })}
              </div>
            </section>
          );
        })()}
      </div>

      <div style={{ height: 40 }} />
    </>
  );
}

function patternNodeLabel(canonicalId: string): string {
  return patternLabel(canonicalId);
}

function Header() {
  return (
    <div className="ohead">
      {/* Wordmark only — and on desktop even that is hidden, because the rail already carries the
          app icon. Putting the mark here a second time was a leftover from swapping the sparkle. */}
      <div className="obrand">
        <span className="nm">Oreia</span>
      </div>
      <div className="olock">
        <IconLock /> Private
      </div>
    </div>
  );
}

const SHORT_DATE = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const shortDate = (date: Date) => SHORT_DATE.format(date);

/**
 * One cell per day, filled by how much was written. Four steps rather than a continuous ramp: at this
 * size the difference between two and three entries is not perceptible anyway, and banding keeps a
 * prolific day from making an ordinary one look empty.
 */
function RhythmGrid({ rhythm }: { rhythm: Rhythm }) {
  const weeks = Math.ceil(rhythm.days.length / 7);

  return (
    <div
      className="orhythm"
      // The grid fills column-first down seven rows, so each column is one calendar week.
      style={{ gridTemplateColumns: `repeat(${weeks}, 1fr)` }}
      role="img"
      aria-label={`${rhythm.activeDays} of the last ${rhythm.days.length} days have an entry`}
    >
      {rhythm.days.map((day) => (
        <i
          key={day.date.toISOString()}
          data-level={Math.min(day.entries, 3)}
          title={`${shortDate(day.date)} · ${day.entries === 0 ? 'nothing' : `${day.entries} ${day.entries === 1 ? 'entry' : 'entries'}`}`}
        />
      ))}
    </div>
  );
}

/**
 * What moved in the portrait while the person was elsewhere.
 *
 * Absent on a first visit, when there is no previous look to measure against, and absent when nothing
 * changed — a card that says "no changes" is a card that costs a glance and gives nothing back.
 */
/**
 * The one card on the dashboard that is purely Oreia's own voice.
 *
 * Two things it must not do. It must not overstate what it looked at — the footer used to print the
 * whole journal's entry count, which read as though every entry supported this one sentence; it now
 * cites the symbol and the number of times that symbol actually appeared. And it must not be a
 * dead end: the reading is *about* something, so the card opens that symbol, where the entries it
 * came from are listed.
 */
function InsightCard({ insight, pattern }: { insight: string; pattern: Pattern | null }) {
  // Canonical ids are namespaced ("symbol:water"); the namespace is internal bookkeeping.
  const symbol = pattern
    ? pattern.canonicalId.includes(':')
      ? pattern.canonicalId.slice(pattern.canonicalId.indexOf(':') + 1)
      : pattern.canonicalId
    : null;

  const body = (
    <>
      <span className="olabel" style={{ color: 'var(--bento-accent-ink)' }}>I've noticed…</span>
      <h3 className="otitle oinsight-text">{insight}</h3>
      <div className="ometa oinsight-meta">
        <span className="omono">
          {symbol && pattern
            ? `${patternLabel(pattern.canonicalId)} · ${pattern.occurrences} appearances`
            : 'from your own entries'}
        </span>
        {symbol ? (
          <span className="oinsight-go">
            where this came from
            <span className="ocirc"><IconArrow /></span>
          </span>
        ) : null}
      </div>
    </>
  );

  if (!symbol) return <div className="ocard ocard-deep span2 oinsight">{body}</div>;

  return (
    <Link className="ocard ocard-deep span2 oinsight ocard-link" to={pattern?.canonicalId.startsWith('symbol:') ? `/symbols/${encodeURIComponent(symbol)}` : '/self'}>
      {body}
    </Link>
  );
}

function WhileYouWereAway({ changes }: { changes: PortraitChanges | null }) {
  if (!changes || changes.firstVisit || !changes.hasChanges) return null;

  const { newSignals, pendingReviewCount, archetypeMovements, lastViewedAt } = changes;

  return (
    <div className="ocard ocard-bright span2">
      <div className="ohead-row">
        <span className="olabel">While you were away</span>
        {lastViewedAt ? <span className="omono">since {shortDate(new Date(lastViewedAt))}</span> : null}
      </div>

      <div className="omoves">
        {newSignals > 0 ? (
          <div className="omove">
            <span className="omove-mark">{newSignals}</span>
            <span>
              <b>new {newSignals === 1 ? 'signal' : 'signals'}</b> surfaced in your entries
            </span>
          </div>
        ) : null}

        {archetypeMovements.slice(0, 3).map((movement) => (
          <div className="omove" key={movement.key}>
            <span className="omove-mark" aria-hidden="true">
              {movement.direction === 'quieter' ? '↓' : '↑'}
            </span>
            <span>
              <b>{movement.name}</b> facet{' '}
              {movement.direction === 'quieter'
                ? 'has gone quieter'
                : movement.direction === 'new'
                  ? 'has been active'
                  : 'has been more present'}
            </span>
          </div>
        ))}

        {pendingReviewCount > 0 ? (
          <div className="omove">
            <span className="omove-mark">{pendingReviewCount}</span>
            <span>
              <b>{pendingReviewCount === 1 ? 'observation' : 'observations'}</b> waiting for your answer
            </span>
          </div>
        ) : null}
      </div>

      <Link className="orow-link is-brand" to="/self" style={{ marginTop: 16 }}>
        Open Self Discovery
        <span className="ocirc">
          <IconArrow />
        </span>
      </Link>
    </div>
  );
}

function FirstRun({ name, onCompose }: { name: string; onCompose: () => void }) {
  return (
    <>
      <Header />

      <div className="ogreet" style={{ marginBottom: 20 }}>
        <h1>
          Welcome,<em>{name}.</em>
        </h1>
        <div className="gsub">
          <p>
            Your inner world is <em>waiting</em> to be written.
          </p>
        </div>
      </div>

      <div className="ocard ocard-bright ofirst-hero" style={{ marginBottom: 14 }}>
        <span className="olabel" style={{ color: 'var(--accent-1)' }}>Begin with a conversation</span>
        <h3 className="htitle" style={{ marginTop: 10 }}>
          One dream, one moment. Oreia <em>grows</em> from whatever you give it.
        </h3>
        <p className="hsub">
          There's nothing to set up and nothing to perfect — just write a little, and your portrait
          starts forming.
        </p>
        <button className="octa" onClick={onCompose}>
          <IconSpark /> Talk through your first entry
        </button>
      </div>

      <PersonalPractice />

      <div style={{ height: 40 }} />
    </>
  );
}
